import {Attachment, AttachmentType, ExpertEstimateState as EstimateState, Location, Photo} from '@/model/api';
import {ErrorWithUserMessage} from '@/model/errors/ErrorWithUserMessage';
import {Estimate} from '@/model/Estimate';
import {HttpClient} from '@/model/http';
import {isLeft, isRight, left, right} from 'fp-ts/lib/Either';

interface CalculateResponse {
    status?: 'calculating';
    calculationUrl?: string;
    aiFallback?: boolean;
}

interface CalculationStatusResponse {
    status: 'calculating' | 'calculated' | 'failed';
    calculationUrl?: string;
}

interface TemplateForNewEstimate {
    id: string;
    created: false;
    offerType: string;
    estateType: string;
    estimateType: string;
}

interface ExistingEstimate {
    id: string;
    created: true;
}

export default class ExpertEstimate implements Estimate<EstimateState> {
    private static readonly BASE_ULR = '/api/expert-estimate/estimates';
    private static readonly HEADERS = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    };

    private onCalculatingStartCallback?: () => void;
    private onCalculatingErrorCallback?: () => void;
    private onCalculatingFailedCallback?: () => void;
    private onAiFallbackCallback?: () => void;

    constructor(
        private estimate: TemplateForNewEstimate | ExistingEstimate,
        private readonly http: HttpClient,
    ) {
    }

    public onCalculatingStart(callback: () => void): void {
        this.onCalculatingStartCallback = callback;
    }

    public onCalculatingError(callback: () => void): void {
        this.onCalculatingErrorCallback = callback;
    }

    public onCalculatingFailed(callback: () => void): void {
        this.onCalculatingFailedCallback = callback;
    }

    public onAiFallback(callback: () => void): void {
        this.onAiFallbackCallback = callback;
    }

    public async uploadPhoto(file: File, position: number): Promise<Photo> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('position', String(position));

        const response = await this.http.safeRequest<Photo>(
            this.estimateUrl(this.getExistingEstimateId()) + '/photos',
            {
                method: 'POST',
                body: formData,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    public async changePhotoDescription(photoId: string, description: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId + '/description',
            {
                method: 'PUT',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify({description}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async removePhoto(photoId: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId,
            {
                method: 'DELETE',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async rotatePhoto(photoId: string, angle: number): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId + '/rotate',
            {
                method: 'POST',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify({angle}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async changePhotoPosition(photoId: string, position: number): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/photos/' + photoId + '/position/' + position,
            {
                method: 'GET',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async addAttachmentType(type: AttachmentType): Promise<void> {
        const response = await this.http.safeRequest(
            this.estimateUrl(this.getExistingEstimateId()) + '/attachments?type=' + type,
            {
                method: 'POST',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async removeAttachmentType(type: AttachmentType): Promise<void> {
        const response = await this.http.safeRequest(
            this.estimateUrl(this.getExistingEstimateId()) + '/attachments?type=' + type,
            {
                method: 'DELETE',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    /**
     * Moves the attachment among the attachments of its type
     */
    public async moveAttachment(attachmentId: string, position: number): Promise<void> {
        const response = await this.http.safeRequest(
            this.estimateUrl(this.getExistingEstimateId()) + '/attachments/' + attachmentId + '/move',
            {
                method: 'PUT',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify({position}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async changeAttachmentPosition(type: AttachmentType, position: number): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/attachments/' + type + '/position/' + position,
            {
                method: 'GET',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    /**
     * Returns the saved files, a PDF is saved as the images of its pages
     */
    public async uploadAttachment(type: AttachmentType, file: File, position: number): Promise<Attachment[]> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('position', String(position));

        const response = await this.http.safeRequest<Attachment[]>(
            this.estimateUrl(this.getExistingEstimateId()) + '/attachments?type=' + type,
            {
                method: 'POST',
                body: formData,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    public async removeAttachment(attachmentId: string): Promise<void> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest(
            this.estimateUrl(estimateId) + '/attachments/' + attachmentId,
            {
                method: 'DELETE',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }
    }

    public async updateParameters(description: object): Promise<EstimateState> {
        const estimateId = this.getExistingEstimateId();

        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimateId) + '/parameters',
            {
                method: 'PATCH',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify(description),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    public async selectLocation(location: Location): Promise<EstimateState> {
        const estimate = this.estimate;

        if (estimate.created) {
            return this.updateLocation(estimate.id, location);
        }

        return this.createNewEstimate(estimate, location);
    }

    public async createEstimate() {
        const response = await this.http.safeRequest<CalculateResponse>(
            this.estimateUrl(this.getExistingEstimateId()) + '/calculate',
            {
                method: 'POST',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            return response;
        }

        const json = response.right;

        if (json.calculationUrl) {
            return right(json.calculationUrl);
        }

        this.onCalculatingStartCallback?.();

        return this.pollForCalculation();
    }

    public async createEstimateWithoutLlm() {
        const response = await this.http.safeRequest<CalculateResponse>(
            this.estimateUrl(this.getExistingEstimateId()) + '/calculate?withoutLlm=1',
            {
                method: 'POST',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            return response;
        }

        const json = response.right;

        if (json.calculationUrl) {
            return right(json.calculationUrl);
        }

        return left(new ErrorWithUserMessage('Nepodařilo se vypočítat odhad.'));
    }

    public async recalculateWithNewEstates(): Promise<void> {
        const response = await this.http.safeRequest<CalculateResponse>(
            this.estimateUrl(this.getExistingEstimateId()) + '/recalculate',
            {
                method: 'POST',
                headers: ExpertEstimate.HEADERS,
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        const json = response.right;

        if (json.calculationUrl) {
            window.location.href = json.calculationUrl;
            return;
        }

        if (json.status === 'calculating') {
            if (json.aiFallback) {
                this.onAiFallbackCallback?.();
            }

            this.onCalculatingStartCallback?.();
            const result = await this.pollForCalculation();
            if (isRight(result)) {
                window.location.href = result.right;
            }
        }
    }

    public async getEstimate(): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(this.estimateUrl(this.getExistingEstimateId()));

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    private async pollForCalculation() {
        const statusUrl = this.estimateUrl(this.getExistingEstimateId()) + '/calculation-status';
        const maxAttempts = 60;

        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, 3000));

            const response = await this.http.safeRequest<CalculationStatusResponse>(
                statusUrl,
                {method: 'GET', headers: ExpertEstimate.HEADERS},
            );

            if (isRight(response) && response.right.status === 'calculated' && response.right.calculationUrl) {
                return right(response.right.calculationUrl);
            }

            if (isRight(response) && response.right.status === 'failed') {
                this.onCalculatingFailedCallback?.();

                return left(new ErrorWithUserMessage('Automatický výběr nemovitostí selhal.'));
            }
        }

        this.onCalculatingErrorCallback?.();

        return left(new ErrorWithUserMessage('Kalkulace trvá déle než obvykle. Zkuste obnovit stránku.'));
    }

    private async updateLocation(estimateId: string, location: Location): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimateId) + '/location',
            {
                method: 'PUT',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify({id: location.id}),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        return response.right;
    }

    private async createNewEstimate(estimate: TemplateForNewEstimate, location: Location): Promise<EstimateState> {
        const response = await this.http.safeRequest<EstimateState>(
            this.estimateUrl(estimate.id),
            {
                method: 'PUT',
                headers: ExpertEstimate.HEADERS,
                body: JSON.stringify({
                    estateType: estimate.estateType,
                    offerType: estimate.offerType,
                    location: location.id,
                    estimateType: estimate.estimateType,
                }),
            },
        );

        if (isLeft(response)) {
            throw response.left;
        }

        this.estimate = {
            id: estimate.id,
            created: true,
        };

        return response.right;
    }

    private estimateUrl(estimateId: string): string {
        return `${ExpertEstimate.BASE_ULR}/${estimateId}`;
    }

    private getExistingEstimateId(): string {
        const estimate = this.estimate;

        if (estimate.created) {
            return estimate.id;
        }

        throw new Error('Estimate ID not set, estimate wasn\'t created yet');
    }
}
